import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { selectionService } from "../selection";
import {
  DraggerType,
  type Point2D,
  type Point3D,
  type DraggerConfig,
  type SnapOptions,
  type TypedInputSpec,
} from "./types";
import { applyEntityProps } from "../entities/properties";
import { deleteObj } from "../core/delete";
import { createHatchFromEntity } from "../database/hatch";
import { createTextAtPosition, MTextAttachment } from "../managers/creation";

/**
 * Abstract base class for all draggers.
 * Provides lifecycle management and coordinate state.
 *
 * Note: Draggers do NOT bind their own events. Events are routed
 * through InteractionManager which calls handleStart/handleMove/handleEnd.
 */
export abstract class BaseDragger {
  protected viewer: DRDrawingViewer;
  protected config: DraggerConfig;

  protected isActive = false;
  protected isDragging = false;

  // Screen coordinates during drag
  protected startScreen: Point2D | null = null;
  protected currentScreen: Point2D | null = null;

  // World coordinates during drag
  protected startWorld: Point3D | null = null;
  protected currentWorld: Point3D | null = null;

  // Collected points for polyline-style draggers
  protected points: Point3D[] = [];

  /** The type identifier for this dragger */
  abstract readonly type: DraggerType;

  constructor(viewer: DRDrawingViewer, config: DraggerConfig = {}) {
    this.viewer = viewer;
    this.config = config;
  }

  // ============== Lifecycle ==============

  /** Called when dragger is activated */
  activate(): void {
    if (this.isActive) return;
    this.isActive = true;
    this.onActivate();
  }

  /** Called when dragger is deactivated */
  deactivate(): void {
    if (!this.isActive) return;
    this.isActive = false;
    this.clear();
    this.onDeactivate();
  }

  /** Override for activation setup */
  protected onActivate(): void {}

  /** Override for deactivation cleanup */
  protected onDeactivate(): void {}

  // ============== Public Handlers (called by InteractionManager) ==============

  /**
   * Handle drag start. Called by InteractionManager.
   */
  handleStart(screen: Point2D, world: Point3D, ev: PointerEvent): void {
    if (!this.isActive) return;

    // Check pen-only mode
    if (this.config.penOnly && ev.pointerType !== "pen") {
      return;
    }

    this.isDragging = true;
    this.startScreen = screen;
    this.startWorld = world;
    this.currentScreen = screen;
    this.currentWorld = world;

    this.onStart(screen, world, ev);
  }

  /**
   * Handle drag move. Called by InteractionManager.
   */
  handleMove(screen: Point2D, world: Point3D, ev: PointerEvent): void {
    if (!this.isActive || !this.isDragging) return;

    this.currentScreen = screen;
    this.currentWorld = world;

    this.onDrag(screen, world, ev);
  }

  /**
   * Handle drag end. Called by InteractionManager.
   */
  handleEnd(screen: Point2D, world: Point3D, ev: PointerEvent): void {
    if (!this.isActive || !this.isDragging) return;

    this.currentScreen = screen;
    this.currentWorld = world;

    this.onEnd(screen, world, ev);
    this.isDragging = false;
  }

  /**
   * Handle the pointer leaving the canvas bounds mid-drag. Called by
   * InteractionManager instead of handleEnd() when the gesture ends because
   * the pointer left the canvas rather than a normal release inside it.
   * Defaults to the same behavior as a normal release (commit using the
   * last in-bounds point) — override to abort the in-progress point instead.
   */
  handleLeave(screen: Point2D, world: Point3D, ev: PointerEvent): void {
    this.handleEnd(screen, world, ev);
  }

  /**
   * Handle drag cancel. Called by InteractionManager.
   */
  handleCancel(): void {
    if (!this.isActive || !this.isDragging) return;

    this.onCancel();
    this.isDragging = false;
  }

  /**
   * Handle a pure mouse hover (button up, no active gesture) while a
   * placement is pending — e.g. point A already fixed in two-point mode, or
   * a polyline's last committed vertex. Called by
   * InteractionManager.handleHover() only when getSnapReferencePoint()
   * returns non-null, i.e. never mid-gesture (that goes through
   * handleMove() instead). No-op by default; override in
   * TwoPointDragger/SequentialDragger/DualModeDragger to keep the preview
   * (rubber band) following the cursor between clicks.
   */
  handleHoverMove(_screen: Point2D, _world: Point3D): void {}

  // ============== Abstract Methods ==============

  /** Called on drag start */
  protected abstract onStart(
    screen: Point2D,
    world: Point3D,
    ev: PointerEvent,
  ): void;

  /** Called on drag move */
  protected abstract onDrag(
    screen: Point2D,
    world: Point3D,
    ev: PointerEvent,
  ): void;

  /** Called on drag end */
  protected abstract onEnd(
    screen: Point2D,
    world: Point3D,
    ev: PointerEvent,
  ): void;

  /** Called when drag is cancelled */
  protected onCancel(): void {
    this.clear();
  }

  // ============== Utility Methods ==============

  /** Clear drag state */
  protected clear(): void {
    this.isDragging = false;
    this.startScreen = null;
    this.currentScreen = null;
    this.startWorld = null;
    this.currentWorld = null;
    this.points = [];
  }

  /** Get the module reference */
  protected get module(): DrawingModuleType {
    return this.viewer.module;
  }

  /**
   * Apply the active NoteConfig properties to an entity.
   * Call this after creating an entity to style it according to the current configuration.
   */
  protected applyNoteConfigToEntity(
    entityId: DrawingModule.OdDbObjectId,
    options: { ignoreLinetype?: boolean } = {},
  ): void {
    const db = this.viewer.database;
    if (!db) return;

    const config = this.viewer.activeNoteConfig;
    const props = options.ignoreLinetype
      ? { ...config.props, linetype: undefined }
      : config.props;
    applyEntityProps(this.module, db, entityId, props);
  }

  /**
   * Get the current label from NoteConfig (if any).
   */
  protected get noteConfigLabel(): string | undefined {
    return this.viewer.activeNoteConfig.label;
  }

  /**
   * Get the current hatch configuration from NoteConfig (if any).
   */
  protected get noteConfigHatch() {
    return this.viewer.activeNoteConfig.hatch;
  }

  /**
   * Create a hatch for a closed entity if configured in NoteConfig.
   * Does NOT add to undo stack - caller is responsible for including hatch in their command.
   *
   * @param boundaryEntityId - The closed entity to use as boundary
   * @returns The hatch entity ID, or null if no hatch was created
   */
  protected createHatchForEntity(
    boundaryEntityId: DrawingModule.OdDbObjectId,
  ): DrawingModule.OdDbObjectId | null {
    const db = this.viewer.database;
    if (!db) return null;

    const noteConfig = this.viewer.activeNoteConfig;
    if (!noteConfig.hatch) return null;

    const hatchResult = createHatchFromEntity(
      this.module,
      db,
      boundaryEntityId,
      noteConfig.hatch,
      noteConfig.props,
    );
    if (!hatchResult) return null;

    deleteObj(hatchResult.hatch);

    return hatchResult.entityId;
  }

  /**
   * Create a text label at the given world position if label mode is enabled.
   * Uses the active NoteConfig for text content and styling.
   * @returns The entity ID of the created text, or null if label mode is off
   */
  protected createLabelIfEnabled(
    worldX: number,
    worldY: number,
  ): DrawingModule.OdDbObjectId | null {
    if (!this.viewer.labelEnabled) return null;

    const db = this.viewer.database;
    if (!db) return null;

    const config = this.viewer.activeNoteConfig;
    if (!config.props.textstyle) return null;

    const text = config.label || config.name.substring(0, 2);

    const entityId = createTextAtPosition(
      this.module,
      db,
      worldX,
      worldY,
      text,
      config.props.textstyle,
      config.props.textsize,
      MTextAttachment.kMiddleCenter,
    );

    applyEntityProps(this.module, db, entityId, config.props);

    return entityId;
  }

  /**
   * Set the autoSelect flag.
   * When true, entities created by this dragger will be automatically selected.
   */
  setAutoSelect(enabled: boolean): void {
    this.config.autoSelect = enabled;
  }

  /**
   * Get the current autoSelect setting (defaults to true).
   */
  get autoSelect(): boolean {
    return this.config.autoSelect ?? true;
  }

  /**
   * Select the created entity (and optional additional entities) if autoSelect is enabled.
   * Call this after creating an entity to optionally select it.
   *
   * @param entityId The primary entity to select
   * @param additionalIds Optional extra entities to include in the selection (e.g. label)
   */
  protected selectCreatedEntity(
    entityId: DrawingModule.OdDbObjectId,
    additionalIds: DrawingModule.OdDbObjectId[] = [],
  ): void {
    if (this.config.autoSelect === false || !entityId) {
      return;
    }

    const module = this.module;
    const db = this.viewer.database;
    if (!module || !db) return;

    const allIds = [entityId, ...additionalIds];
    if (allIds.length === 1) {
      selectionService.selectEntity(entityId, module, db);
    } else {
      selectionService.selectEntities(allIds, module, db);
    }

    // Update the viewer
    this.viewer.update("BaseDragger.selectCreatedEntity");
  }

  /**
   * Get the snap reference point for ortho/polar constraints.
   * Override in subclasses to provide a meaningful reference
   * (e.g., the last committed vertex in sequential drawing).
   * @returns The reference point, or null to use InteractionManager's default
   */
  getSnapReferencePoint(): Point3D | null {
    return null;
  }

  /**
   * When getSnapReferencePoint() returns null, should the point where the
   * gesture STARTS be the ortho/polar reference? Yes for a single-stroke
   * shape: the start is the shape's first point and the end is constrained
   * against it. No for points placed on release (two-point mode, point-by-point
   * polyline, base point): the drag only carries the point to where the user
   * lets go, so it must not be aligned with its own starting position.
   */
  snapsToGestureStart(): boolean {
    return true;
  }

  /** Dispose the dragger */
  dispose(): void {
    this.deactivate();
  }

  getSnapOptions(): SnapOptions | undefined {
    return this.config.snapOptions;
  }

  /**
   * Toggle two-point sequential placement mode (press-drag-release for point
   * A, then again for point B, instead of one continuous stroke). No-op by
   * default; only meaningful for draggers built on DualModeDragger. Safe to
   * call on any dragger, e.g. via DraggerManager's bulk broadcast.
   */

  setTwoPointMode(_enabled: boolean): void {}

  /**
   * Whether this dragger currently has an uncommitted placement in progress
   * that a UI cancel button should offer to discard — e.g. point A already
   * placed in two-point mode, awaiting point B. False by default; only
   * meaningful for draggers built on TwoPointDragger/DualModeDragger.
   */
  hasPendingPlacement(): boolean {
    return false;
  }

  /**
   * Subscribe to hasPendingPlacement() changes, so UI (e.g. a floating
   * cancel button — touch/tablet users have no Escape key) can react without
   * polling. Default no-op unsubscribe.
   */

  onPendingPlacementChange(_handler: (pending: boolean) => void): () => void {
    return () => {};
  }

  /** Discard any pending placement (e.g. point A). No-op by default. */
  cancelPendingPlacement(): void {}

  /**
   * i18n key for a short prompt describing the point the tool is currently
   * waiting for ("pick a base point", "pick a destination"), or null if the
   * tool has no multi-step prompt. Only meaningful for BasePointDragger-based
   * edit tools, where the step sequence isn't self-evident (SCALE takes three
   * points).
   */
  getStepPrompt(): string | null {
    return null;
  }

  /**
   * Subscribe to getStepPrompt() changes, so an on-canvas hint can follow the
   * tool from step to step without polling. Default no-op unsubscribe.
   */
  onStepPromptChange(_handler: (prompt: string | null) => void): () => void {
    return () => {};
  }

  /**
   * Attempt to intercept an undo request before it reaches CommandHistory —
   * called by HistoryManager.undo() so draggers with in-progress multi-step
   * state (e.g. SequentialDragger placing polyline points) can undo their
   * own last step and keep dragger-internal state (worldPoints/commandCount)
   * in sync with the command stack, instead of the command stack and the
   * dragger silently diverging (each undoing a different amount).
   * @returns true if the dragger handled the undo itself (caller must not
   * also call CommandHistory.undo()); false to let normal undo proceed.
   * False by default.
   */
  handleUndo(): boolean {
    return false;
  }

  /**
   * Attempt to intercept a redo request before it reaches CommandHistory —
   * called by HistoryManager.redo(), symmetric to handleUndo() above.
   * @returns true if the dragger handled the redo itself (caller must not
   * also call CommandHistory.redo()); false to let normal redo proceed.
   * False by default.
   */
  handleRedo(): boolean {
    return false;
  }

  // ============== Typed numeric input ==============
  //
  // The single channel every tool speaks to the keypad through — the draw
  // tools (next vertex, relative to the last one) and the base-point edit
  // tools (destination, angle, factor, length) alike. A step advertises one or
  // more TypedInputModes; the pad shows a mode selector only when there is
  // more than one, and names the chosen one back on every preview/commit so
  // the dragger knows how to read the numbers.

  /**
   * The modes this dragger accepts at its current step, with their live
   * values, or null when typing makes no sense right now (wrong tool, or a
   * step that only a pick can satisfy). Null by default.
   */
  getTypedInputSpec(): TypedInputSpec | null {
    return null;
  }

  private typedInputHandlers: Array<(spec: TypedInputSpec | null) => void> = [];

  /**
   * Subscribe to getTypedInputSpec() changes — both step changes (the mode
   * list itself changes) and live value updates during a drag — so the keypad
   * can mirror them without polling.
   *
   * Implemented here rather than per-subclass because all three families that
   * feed the pad (BasePointDragger, SequentialDragger, TwoPointDragger) need
   * exactly this list; they only differ in what getTypedInputSpec() returns and
   * when they call emitTypedInputChange().
   */
  onTypedInputChange(handler: (spec: TypedInputSpec | null) => void): () => void {
    this.typedInputHandlers.push(handler);
    return () => {
      const index = this.typedInputHandlers.indexOf(handler);
      if (index >= 0) this.typedInputHandlers.splice(index, 1);
    };
  }

  /** Push the current spec to subscribers. Call after any step or value change. */
  protected emitTypedInputChange(): void {
    const spec = this.getTypedInputSpec();
    for (const handler of this.typedInputHandlers) handler(spec);
  }

  /**
   * Preview typed values without committing — updates the live preview exactly
   * as moving the pointer would. `modeId` names which of getTypedInputSpec()'s
   * modes the values belong to. No-op by default.
   */

  previewTypedInput(_modeId: string, _values: Record<string, number>): void {}

  /**
   * Commit the current step from typed values — exactly as releasing a pointer
   * on the implied point would. No-op by default.
   */

  commitTypedInput(_modeId: string, _values: Record<string, number>): void {}
}
